import React, { useContext, useEffect, useState, useCallback } from 'react';
import {
  Paper,
  Grid,
  TextField,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
} from '@mui/material';
import CreateStockConsumptionDialog from '../../popups/CreateStockConsumptionDialog';
import EditStockConsumptionDialog from '../../popups/EditStockConsumptionDialog';
import EditIcon from '@mui/icons-material/Edit';
import styles from './styles.module.css';
import DataTable from '../../utils/DataTable/';
import Info from '../../utils/Alert/Info';
import Error from '../../utils/Alert/Error';
import { SUBSCRIPTION_TYPE } from '../../../const';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import DateTimeHelpers from '../../../helpers/DateTimeHelpers';
import { ShopContext } from '../../../Context/ShopContext';
import AlertHelper from '../../../helpers/AlertHelper';
import RestrictionInfo from '../../utils/Alert/RestrictionInfo';
import StockReportService from '../../../services/StockReportService';
import { WarehouseContext } from '../../../Context/WarehouseContext';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import withConsoleBase from '../../utils/ConsoleBase/withConsoleBase';
import CsvHelper from '../../../helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import { saveAs } from 'file-saver';
import SyncIcon from '@mui/icons-material/Sync';
import Grayout from '../../utils/GrayOut/Grayout';
import DateTimeRange from '../../utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from '../../../services/DynamicDateRangeService';
import { roundOff } from 'helpers/NumberFormatHelper';
import { useTranslation } from 'react-i18next';
import { selectedTabContext } from '../../../Context/SelectedTabContext';

function StockConsumptionReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { setSelectedTab, setSelectedSubTab, setSections } = useContext(selectedTabContext);
  const { warehouses, getWarehouses } = useContext(WarehouseContext);

  useEffect(() => {
    setSelectedTab('stock-consumption-report');
    setSelectedSubTab(null);
    setSections((prev) => ({ ...prev, operations: true }));
  }, [setSelectedTab, setSelectedSubTab, setSections]);

  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [stocks, setStocks] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [errorMsg, setErrorMsg] = useState();
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const isWarehouseActive = shop && !featureIsExpired(shop?.warehouse_validity);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');
  const [prevFilterData, setPrevFilterData] = useState({
    fromDate,
    toDate,
    warehouseId: '',
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedStockLog, setSelectedStockLog] = useState(null);

  useEffect(() => {
    if (isWarehouseActive && getWarehouses) {
      getWarehouses();
    }
  }, [isWarehouseActive, getWarehouses]);

  const handleEditClick = (item) => {
    setSelectedStockLog(item);
    setIsEditDialogOpen(true);
  };

  const subscriptionType = shop?.subscription?.type;

  const CSV_COLUMNS = {
    item_plu: {
      title: tCommon('PLU'),
      getValue: (item) => item.item?.plu || '',
    },
    item_name: {
      title: tCommon('Item'),
      getValue: (item) => item.item?.name || '',
    },
    ...(isWarehouseActive
      ? {
          warehouse: {
            title: tCommon('Warehouse'),
            getValue: (item) => item.warehouse?.name || '',
          },
        }
      : {}),
    date: {
      title: tCommon('Date'),
      getValue: (item) => DateTimeHelpers.getDate(item.created_at) || '',
    },
    time: {
      title: tCommon('Time'),
      getValue: (item) => item.created_at ? DateTimeHelpers.convertTo12HourTime(item.created_at) : '',
    },
    quantity: {
      title: tCommon('Quantity'),
      getValue: (item) => (item.quantity ? Math.abs(roundOff(item.quantity)) : '0'),
    },
    description: tCommon('Description'),
  };

  const loadStockReports = async ({ _fromDate = fromDate, _toDate = toDate, _warehouseId = selectedWarehouseId } = {}) => {
    setLoadingIndicator(true);
    try {
      const data = {
        start_date: DateTimeHelpers.getTimezoneOffsetDate(_fromDate),
        end_date: DateTimeHelpers.getTimezoneOffsetDate(_toDate),
        type: 10, // Consumption type
      };
      if (_warehouseId && _warehouseId !== 'all') {
        data.warehouse_id = _warehouseId;
      }
      const resp = await StockReportService.getStockConsumptionReport(data);
      setStocks(resp || []);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    }
    setLoadingIndicator(false);
  };

  const generateStockReport = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('STOCK_CONSUMPTION_REPORT');

    setFromDate(dynamicStartDate);
    setToDate(dynamicEndDate);

    loadStockReports({
      _fromDate: dynamicStartDate,
      _toDate: dynamicEndDate,
      _warehouseId: selectedWarehouseId,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      fromDate: dynamicStartDate,
      toDate: dynamicEndDate,
      warehouseId: selectedWarehouseId,
    });
  };

  const isAnyChangeOnStockReportFilters = useCallback(() => {
    return (
      !DateTimeHelpers.isSameDate(fromDate, prevFilterData.fromDate) ||
      !DateTimeHelpers.isSameDate(toDate, prevFilterData.toDate) ||
      selectedWarehouseId !== prevFilterData.warehouseId
    );
  }, [fromDate, toDate, selectedWarehouseId, prevFilterData.fromDate, prevFilterData.toDate, prevFilterData.warehouseId]);

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(stocks, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `STOCK-CONSUMPTION-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const headerData = [
    {
      label: tCommon('Date'),
      id: 'created_at',
      type: 'callback',
      viewRender: (item) => {
        return DateTimeHelpers.getDate(item.created_at) || '';
      },
    },
    {
      label: tCommon('Time'),
      id: 'time',
      type: 'callback',
      viewRender: (item) => {
        return item.created_at ? DateTimeHelpers.convertTo12HourTime(item.created_at) : '';
      },
    },
    {
      label: tCommon('PLU'),
      id: 'item_plu',
      type: 'callback',
      viewRender: (item) => item.item?.plu || '',
    },
    {
      label: tCommon('Item Name'),
      id: 'item_name',
      type: 'callback',
      viewRender: (item) => item.item?.name || '',
    },
    ...(isWarehouseActive
      ? [
          {
            label: tCommon('Warehouse') || 'Warehouse',
            id: 'warehouse',
            type: 'callback',
            viewRender: (item) => item.warehouse?.name || item.warehouse_name || '-',
          },
        ]
      : []),
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'callback',
      viewRender: (item) => (item.quantity ? Math.abs(roundOff(item.quantity)) : '0'),
    },
    {
      label: tCommon('Description'),
      id: 'description',
      type: 'text',
    },
    {
      label: tCommon('Action'),
      id: 'action',
      type: 'callback',
      viewRender: (item) => (
        <IconButton size="small" color="primary" onClick={() => handleEditClick(item)}>
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  useEffect(() => {
    generateStockReport();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnStockReportFilters());
  }, [isAnyChangeOnStockReportFilters, isShowGenerateButton]);

  const filteredStocks = stocks.filter((stock) => {
    const searchLower = filterText.toLowerCase();
    return (
      (stock.item?.name || '').toLowerCase().includes(searchLower) ||
      String(stock.item?.plu || '').includes(searchLower) ||
      (stock.description || '').toLowerCase().includes(searchLower) ||
      (isWarehouseActive && (stock.warehouse?.name || stock.warehouse_name || '').toLowerCase().includes(searchLower))
    );
  });

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Stock')}
          <span className={styles.menuTitle}>{tCommon('Consumption')}</span>
        </h2>

        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
          <div style={{ justifyContent: 'flex-end' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Export As')}</label>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#17a2b8' }}
                onClick={() => setIsCreateDialogOpen(true)}
              >
                {tCommon('Create Consumption')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={toCsvBtnPressed}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                csv
              </Button>
            </div>
          </div>
        )}
      </div>
      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
        <RestrictionInfo
          title={tCommon('Feature not available')}
          content={tCommon('To get stock report options upgrade subscription to premium')}
        />
      )}
      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
        <>
          <div className={styles.changeable}>
            <Grid container spacing={1} alignItems="flex-end">
              <Grid item xs={12}>
                <Typography variant="h6">
                  {tReport('Stock consumption report from {{fromDate}} to {{toDate}}', {
                    fromDate: DateTimeHelpers.convertDateToDMY(fromDate),
                    toDate: DateTimeHelpers.convertDateToDMY(toDate),
                  })}
                </Typography>
              </Grid>
              <Grid item xs={12} md={isWarehouseActive ? 2.4 : 3}>
                <DateTimeRange
                  setFilterFromDate={setFromDate}
                  setFilterToDate={setToDate}
                  storageKey="STOCK_CONSUMPTION_REPORT"
                />
              </Grid>
              <Grid item xs={12} md={isWarehouseActive ? 2.4 : 3}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={tCommon('Search items')}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </Grid>
              {isWarehouseActive && (
                <Grid item xs={12} md={2.4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    variant="outlined"
                    label={tCommon('Warehouse') || 'Warehouse'}
                    value={selectedWarehouseId}
                    onChange={(e) => setSelectedWarehouseId(e.target.value)}
                  >
                    <MenuItem value="">{tCommon('All Warehouses') || 'All Warehouses'}</MenuItem>
                    {warehouses.map((w) => (
                      <MenuItem key={w.id} value={String(w.id)}>
                        {w.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              <Grid item xs={12} md={isWarehouseActive ? 2.4 : 3}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" style={{ marginBottom: 4, fontWeight: 500 }}>
                    {tCommon('Generate Report')}
                  </Typography>

                  <Button
                    variant="contained"
                    style={{
                      backgroundColor: '#00a65a',
                      color: '#fff',
                      height: 40,
                      minWidth: 160,
                    }}
                    onClick={generateStockReport}
                    disabled={isShowGenerateButton === false || loadingIndicator}
                    startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
                  >
                    {tCommon('Generate')}
                  </Button>
                </div>
              </Grid>
            </Grid>
          </div>
          {errorMsg && (
            <div className={styles.marginTop}>
              <Error title={errorMsg} />
            </div>
          )}
          {isShowGenerateButton === null ? (
            <Info
              title={tReport('Set filters and click generate button')}
              content={tReport(
                'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
              )}
            />
          ) : filteredStocks && filteredStocks.length ? (
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <div className={styles.tableContainer}>
                <Grayout open={isShowGenerateButton} />
                <DataTable columns={headerData} rows={filteredStocks} rowKey="id" />
              </div>
            </Paper>
          ) : (
            !loadingIndicator && (
              <Info
                title={tReport('Oh no, there are data matching to your filter')}
                content={tReport(
                  'There are no data matching to your filter. Please verify your inputs to generate report'
                )}
              />
            )
          )}
        </>
      )}

      <CreateStockConsumptionDialog
        open={isCreateDialogOpen}
        handleClose={() => setIsCreateDialogOpen(false)}
        handleSuccess={loadStockReports}
      />

      <EditStockConsumptionDialog
        open={isEditDialogOpen}
        handleClose={() => setIsEditDialogOpen(false)}
        handleSuccess={loadStockReports}
        stockLog={selectedStockLog}
      />
    </div>
  );
}

export default withConsoleBase(StockConsumptionReport);
